import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, 
  CircularProgress, TablePagination, TextField, Button
} from '@mui/material';

const List = () => {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  
  // Filter states
  const [filters, setFilters] = useState({
    search: ''
  });

  useEffect(() => {
    fetchFeedback();
  }, [page, rowsPerPage, filters]);

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      const authTokenStr = localStorage.getItem('auth_token_crm');
      const token = authTokenStr ? JSON.parse(authTokenStr)?.token : null;
      
      // Build query parameters
      const queryParams = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...(filters.search && { search: filters.search })
      });

      const response = await fetch(`${process.env.REACT_APP_SERVER_URI}/users/feedback/list?${queryParams}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });
      
      const result = await response.json();
      setFeedback(result.data || []);
      setTotal(result.total || 0);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      setFeedback([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({
      search: ''
    });
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return 'success';
    if (rating >= 3) return 'warning';
    return 'error';
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>Feedback List</Typography>
      
      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            label="Search"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            placeholder="Search by name, feedback..."
            sx={{ minWidth: 300 }}
          />
          
          <Button variant="outlined" onClick={clearFilters}>
            Clear
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Feedback</TableCell>
                <TableCell>Created At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {feedback.map((item) => (
                <TableRow key={item._id}>
                  <TableCell>{item.customer?.name || '-'}</TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        maxWidth: 300, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={item.feedback}
                    >
                      {item.feedback || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(item.createdAt)}</TableCell>
                </TableRow>
              ))}
              {feedback.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">No feedback found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default List;